import { Combobox } from '@base-ui/react/combobox';
import { type Ref } from 'react';
import { Checkbox } from '#components/Input/Checkbox';
import { useDropdownContext } from '../../context/DropdownContext';
import { DropdownToggleButton } from '../DropdownToggleButton/DropdownToggleButton';
import { ItemContent } from '../ItemContent/ItemContent';
import { type FlatItem } from '../../types';
import { styles } from './styles';

type DropdownItemRowProps = {
  item: FlatItem;
  index?: number;
  ariaSetSize?: number;
  ariaPosInSet?: number;
  dataIndex?: number;
  itemRef?: Ref<HTMLDivElement>;
};

export function DropdownItemRow({
  item,
  index,
  ariaSetSize,
  ariaPosInSet,
  dataIndex,
  itemRef,
}: DropdownItemRowProps) {
  const {
    childrenByValue,
    selectedSet,
    partialByValue,
    expandedSet,
    toggleExpanded,
    expandable,
    multiple,
  } = useDropdownContext();
  const isSelected = selectedSet.has(item.value);
  const isPartiallySelected = partialByValue.get(item.value) === true;
  const hasChildren = (childrenByValue.get(item.value) ?? []).length > 0;
  const isExpanded = expandedSet.has(item.value);

  const isSelectable = item.type === 'value';
  const showPartial = isPartiallySelected && !isSelected;

  return (
    <Combobox.Item
      ref={itemRef}
      data-index={dataIndex}
      css={styles.Item({ itemDepth: item.depth, expandable, isSelectable, multiple })}
      index={index}
      value={item.value}
      disabled={!isSelectable}
      aria-setsize={ariaSetSize}
      aria-posinset={ariaPosInSet}
    >
      {isSelectable && multiple && (
        <div css={styles.ItemIndicator}>
          <Checkbox
            checked={isSelected}
            semiChecked={showPartial}
            fullWidth={true}
            stopEventPropagation={false}
          />
        </div>
      )}
      <div css={styles.ItemText({ multiple: multiple })}>
        <ItemContent item={item} />
      </div>
      {hasChildren && expandable && (
        <DropdownToggleButton
          label={item.label}
          isExpanded={isExpanded}
          onToggle={() => {
            toggleExpanded(item.value);
          }}
        />
      )}
    </Combobox.Item>
  );
}
